import React from "react";
import {
  Baby,
  Users,
  Heart,
  UserCheck,
  HandHelping,
  Sparkles,
  Home,
  HeartPulse,
  BookOpen,
  GraduationCap,
  HandCoins,
  Music,
  Compass,
  ArrowUpRight,
  MapPin,
  UserCircle,
} from "lucide-react";

/**
 * Local Ministry Hub — leads with Garland Ebenezer, then links to official
 * Seventh-day Adventist conference resources at Texas Conference, North
 * American Division, and General Conference levels.
 *
 * Local ministry leader / contact / participation details are placeholders
 * ("Information Coming Soon") until the church supplies them.
 */

const NAD_URL = "https://www.nadadventist.org/";
const GC_URL = "https://gc.adventist.org/about-us/departments-services/";
// Note: NAD_URL / GC_URL are kept only as a safe fallback for the bottom
// "Can't Find Your Ministry Fit?" CTA below — never used on individual
// ministry cards (per URL rule: no generic homepages on cards).

const SECTIONS = [
  {
    title: "Worship & Discipleship",
    ministries: [
      {
        icon: BookOpen,
        name: "Sabbath School",
        mission:
          "Sabbath School is where our church family gathers each week to study the Word together. It grounds every age group in Scripture through discussion, prayer, and small-group fellowship.",
        localFocus:
          "At Garland Ebenezer, Sabbath School begins each Saturday at 9:30 AM before Divine Worship. Classes are offered for children, youth, and adults.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/sabbath-school/" },
          { label: "North American Division", url: "https://www.nadadventist.org/departments/sabbath-school-personal-ministries/" },
          { label: "General Conference", url: "https://sabbathschoolpersonalministries.org/" },
        ],
      },
      {
        icon: Sparkles,
        name: "Prayer Ministries",
        mission:
          "Prayer is the heartbeat of our church. Our prayer team lifts up the needs of members, families, and our community — trusting God to move.",
        localFocus:
          "At Garland Ebenezer, we gather for prayer during our weekly programs and are building a prayer chain to intercede for one another throughout the week.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/prayer-ministry/" },
        ],
      },
    ],
  },
  {
    title: "Children & Youth",
    ministries: [
      {
        icon: Baby,
        name: "Children's Ministries",
        mission:
          "We nurture young hearts to love Jesus through age-appropriate Bible learning, songs, crafts, and warm relationships. Every child is precious to God — and to us.",
        localFocus:
          "At Garland Ebenezer, children have a dedicated Sabbath School class each Saturday morning and are encouraged to participate in special children's programs throughout the year.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/childrens-ministries/" },
          { label: "North American Division", url: "https://www.nadadventist.org/departments/childrens-ministries/" },
          { label: "General Conference", url: "https://children.adventist.org/" },
        ],
      },
      {
        icon: Users,
        name: "Youth Ministries",
        mission:
          "We empower teens and young adults to live boldly for Christ through fellowship, service, and discipleship — including Pathfinders and Adventurers where available.",
        localFocus:
          "At Garland Ebenezer, our Youth Corner meets Fridays 8:00–9:00 PM for Bible study, worship, and fellowship, growing our young people in faith and friendship.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/youth-young-adult-ministries/" },
          { label: "North American Division", url: "https://www.nadadventist.org/departments/youth-young-adult-ministries/" },
          { label: "General Conference", url: "https://www.gcyouthministries.org/" },
        ],
      },
    ],
  },
  {
    title: "Family Ministries",
    ministries: [
      {
        icon: Home,
        name: "Family Ministries",
        mission:
          "Strong families are formed on Christ. Family Ministries strengthens marriages, parenting, and the next generation with Bible-based teaching, resources, and encouragement.",
        localFocus:
          "At Garland Ebenezer, we celebrate and support families through worship, fellowship gatherings, and shared prayer for every household in our church.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/family-ministries/" },
          { label: "North American Division", url: "https://www.nadadventist.org/departments/family-ministries/" },
          { label: "General Conference", url: "https://family.adventist.org/" },
        ],
      },
      {
        icon: Heart,
        name: "Women's Ministries",
        mission:
          "A sisterhood of prayer, encouragement, and discipleship rooted in shared faith. Women's Ministries walks alongside every woman in her spiritual journey.",
        localFocus:
          "At Garland Ebenezer, the women of our church encourage one another through fellowship, prayer, and service opportunities.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/womens-ministries/" },
          { label: "North American Division", url: "https://www.nadadventist.org/departments/womens-ministries/" },
          { label: "General Conference", url: "https://women.adventist.org/" },
        ],
      },
      {
        icon: UserCheck,
        name: "Men's Ministries",
        mission:
          "Building godly men who lead their families, serve the church, and bless their community. Men's Ministries calls each brother to walk closely with Christ.",
        localFocus:
          "At Garland Ebenezer, the men of our church support one another in prayer, ministry, and faithful service.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/mens-ministries/" },
        ],
      },
    ],
  },
  {
    title: "Outreach & Evangelism",
    ministries: [
      {
        icon: HandHelping,
        name: "Community Services",
        mission:
          "We love our city through practical acts of service — food drives, seasonal outreach, and compassion in times of need. Ministry begins where people live.",
        localFocus:
          "At Garland Ebenezer, we look for opportunities to bless our Garland and Richardson neighbors through service projects our members lead together.",
        resources: [
          { label: "North American Division", url: "https://www.nadadventist.org/departments/adventist-community-services/" },
        ],
      },
      {
        icon: HeartPulse,
        name: "Health Ministries",
        mission:
          "God cares about the whole person — body, mind, and spirit. Health Ministries shares the Adventist health message as part of our witness to Christ.",
        localFocus:
          "At Garland Ebenezer, we encourage members to pursue healthful living and share health resources rooted in the Adventist health message.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/health-ministries/" },
          { label: "North American Division", url: "https://www.nadadventist.org/departments/health-ministries/" },
          { label: "General Conference", url: "https://www.healthministries.com/" },
        ],
      },
    ],
  },
  {
    title: "Music & Media",
    ministries: [
      {
        icon: Music,
        name: "Music Ministry",
        mission:
          "Music is worship. Our singers, musicians, and worship leaders lift Christ up each Sabbath and prepare hearts to hear God's Word.",
        localFocus:
          "At Garland Ebenezer, our music ministry leads congregational singing and special music during Divine Worship each Sabbath.",
        resources: [],
      },
    ],
  },
  {
    title: "Administration & Support",
    ministries: [
      {
        icon: HandCoins,
        name: "Stewardship",
        mission:
          "Stewardship honors God with the time, talents, and treasures He has entrusted to us. It is worship, not obligation.",
        localFocus:
          "At Garland Ebenezer, tithes and offerings support both our local ministry needs and the wider work of the Seventh-day Adventist Church through the Texas Conference.",
        resources: [
          { label: "Texas Conference", url: "https://texasadventist.org/departments/stewardship/" },
          { label: "North American Division", url: "https://www.nadstewardship.org/" },
          { label: "General Conference", url: "https://stewardship.adventist.org/" },
        ],
      },
      {
        icon: GraduationCap,
        name: "Education",
        mission:
          "Christ-centered education shapes minds, character, and lifelong service. The Adventist school system spans preschool through university on every continent.",
        localFocus:
          "At Garland Ebenezer, we encourage families to explore Adventist education options and support students in their faith and studies.",
        resources: [
          { label: "North American Division", url: "https://www.nadadventist.org/departments/education/" },
          { label: "General Conference", url: "https://education.adventist.org/" },
        ],
      },
    ],
  },
];

const slug = (name) => name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

/** Ministry card — preserves existing card design; content restructured to lead with local church. */
const MinistryCard = ({ ministry, index }) => {
  const { icon: Icon, name, mission, localFocus, resources = [] } = ministry;
  const isNavy = index % 3 === 1;

  return (
    <div
      data-testid={`ministry-card-${slug(name)}`}
      className={`group p-8 rounded-sm border transition-all hover:shadow-lg hover:-translate-y-1 flex flex-col ${
        isNavy
          ? "bg-[#003B5C] text-white border-[#003B5C]"
          : "bg-white text-[#1E1E1E] border-gray-200 hover:border-[#D4A437]"
      }`}
    >
      <div
        className={`w-12 h-12 rounded-sm flex items-center justify-center mb-5 transition-colors ${
          isNavy ? "bg-[#D4A437]" : "bg-[#003B5C] group-hover:bg-[#D4A437]"
        }`}
      >
        <Icon size={22} className="text-white" />
      </div>

      <h3 className={`font-display text-xl font-bold mb-3 ${isNavy ? "text-white" : "text-[#003B5C]"}`}>
        {name}
      </h3>
      <p className={`text-sm leading-relaxed mb-5 ${isNavy ? "text-white/75" : "text-[#1E1E1E]/70"}`}>
        {mission}
      </p>

      {/* Local focus block — Garland Ebenezer first */}
      <div
        className={`text-xs font-bold uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5 ${
          isNavy ? "text-[#D4A437]" : "text-[#D4A437]"
        }`}
      >
        <MapPin size={12} /> At Garland Ebenezer
      </div>
      <p className={`text-sm leading-relaxed mb-4 ${isNavy ? "text-white/85" : "text-[#1E1E1E]/80"}`}>
        {localFocus}
      </p>

      <div
        className={`text-xs leading-relaxed space-y-1 mb-5 ${
          isNavy ? "text-white/70" : "text-[#1E1E1E]/60"
        }`}
      >
        <div className="flex items-start gap-1.5">
          <UserCircle size={12} className="mt-[3px] shrink-0" />
          <span>
            <span className="font-semibold">Local leader:</span> Information coming soon
          </span>
        </div>
        <div>
          <span className="font-semibold">Get involved:</span> Information coming soon —{" "}
          <a
            href="/contact"
            data-testid={`ministry-contact-${slug(name)}`}
            className={`underline underline-offset-2 ${
              isNavy ? "text-[#D4A437] hover:text-white" : "text-[#003B5C] hover:text-[#D4A437]"
            }`}
          >
            contact the church
          </a>
          .
        </div>
      </div>

      {/* Conference resources — always appear AFTER local info */}
      {resources.length > 0 && (
        <div className={`mt-auto pt-4 border-t ${isNavy ? "border-white/15" : "border-gray-100"}`}>
          <div
            className={`text-[10px] font-bold uppercase tracking-[0.22em] mb-3 ${
              isNavy ? "text-white/60" : "text-[#1E1E1E]/50"
            }`}
          >
            Conference Resources
          </div>
          <ul className="space-y-1.5">
            {resources.map((r) => (
              <li key={r.label}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`ministry-resource-${slug(name)}-${slug(r.label)}`}
                  className={`inline-flex items-center justify-between w-full gap-2 text-xs font-semibold py-1 transition-colors ${
                    isNavy
                      ? "text-[#D4A437] hover:text-white"
                      : "text-[#003B5C] hover:text-[#D4A437]"
                  }`}
                >
                  <span>{r.label}</span>
                  <ArrowUpRight size={13} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function Ministries() {
  // Global card index used to preserve the alternating navy-accent styling
  let cardIdx = -1;

  return (
    <div data-testid="ministries-page">
      <section className="bg-[#003B5C] py-28 lg:py-36 relative grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">
            Local Ministry Hub
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white max-w-4xl">
            Find your place.
          </h1>
          <p className="mt-8 text-lg text-white/75 max-w-3xl leading-relaxed">
            Ministries are at the heart of church life. Through worship, discipleship, fellowship,
            outreach, and service, Garland Ebenezer Ghanaian Seventh-day Adventist Church seeks
            to help every member grow in Christ while serving our community. As part of the Texas
            Conference of Seventh-day Adventists, many of our ministries also participate in
            conference-wide training, resources, and mission initiatives that strengthen our local
            ministry.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16 lg:space-y-20">
          {SECTIONS.map((section) => (
            <div key={section.title} data-testid={`ministry-section-${slug(section.title)}`}>
              <div className="mb-8 lg:mb-10">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-3">
                  Section
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#003B5C]">
                  {section.title}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {section.ministries.map((m) => {
                  cardIdx += 1;
                  return <MinistryCard key={m.name} ministry={m} index={cardIdx} />;
                })}
              </div>
            </div>
          ))}

          {/* Broader Adventist resources — final CTA card, styling preserved */}
          <div
            data-testid="ministry-card-cant-find"
            className="group p-10 lg:p-12 rounded-sm border-2 border-[#D4A437] bg-[#F7F8FA] hover:shadow-lg transition-all flex flex-col lg:flex-row lg:items-center gap-8"
          >
            <div className="lg:flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-sm bg-[#D4A437] flex items-center justify-center flex-shrink-0">
                  <Compass size={22} className="text-white" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#003B5C]">
                  Can't Find Your Ministry Fit?
                </h3>
              </div>
              <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 max-w-3xl">
                Explore the many departments, ministries, and services of the worldwide
                Seventh-day Adventist Church and discover where God may be leading you to
                serve, grow, and connect.
              </p>
            </div>
            <a
              href={GC_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ministry-cta-cant-find"
              className="inline-flex items-center justify-center gap-2 bg-[#003B5C] text-white px-8 py-4 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all whitespace-nowrap self-start lg:self-auto"
            >
              Conference Resources
              <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="text-center max-w-3xl mx-auto">
            <h3 className="font-display text-3xl sm:text-4xl font-bold text-[#003B5C]">
              Want to get involved here at Ebenezer?
            </h3>
            <p className="mt-4 text-lg text-[#1E1E1E]/70">
              Reach out — we'd love to help you discover your calling and connect you with the right ministry.
            </p>
            <a
              href="/contact"
              data-testid="ministries-contact-cta"
              className="inline-block mt-8 bg-[#003B5C] text-white px-8 py-4 rounded-sm font-medium hover:bg-[#002A42] transition-all"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
